import { Crown, Shield, Trash2 } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { useDeleteUser } from '../../hooks/queries/useUserQueries';
import { IUser } from '../../types';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

interface IUserDeleteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  user: IUser | null;
  onDeleted?: () => void;
}

const UserDeleteDialog = ({
  isOpen,
  onClose,
  user,
  onDeleted,
}: IUserDeleteDialogProps) => {
  const { t } = useTranslation();
  const deleteUser = useDeleteUser();
  // The API refuses this too (admin.user_owns_spaces); saying so up front
  // beats letting the admin confirm and read it from an error toast
  const ownsSpaces = user?.role === 'space_owner';

  const handleConfirm = async () => {
    if (!user) return;

    await deleteUser.mutateAsync(user.id);
    onClose();
    onDeleted?.();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="sm:max-w-[420px]"
        data-testid="UserManagement_List_deleteModal"
      >
        <DialogHeader>
          <DialogTitle>{t('users.delete_title')}</DialogTitle>
          <DialogDescription>
            {t('users.delete_confirm', { name: user?.displayName ?? '' })}
          </DialogDescription>
        </DialogHeader>
        {ownsSpaces ? (
          <div
            className="bg-amber-50 border border-amber-200 rounded-lg p-4"
            data-testid="UserManagement_List_deleteModalOwnerNotice"
          >
            <div className="flex items-start gap-3">
              <Crown className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-900">
                  {t('users.delete_blocked_space_owner')}
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  {t('users.delete_blocked_space_owner_text')}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-900">
                  {t('users.delete_warning')}
                </p>
                <p className="text-sm text-red-700 mt-1">
                  {t('users.delete_warning_text')}
                </p>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="UserManagement_List_deleteModalCancelButton"
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={deleteUser.isPending || ownsSpaces}
            data-testid="UserManagement_List_deleteModalConfirmButton"
          >
            <Trash2 className="w-4 h-4" />
            {deleteUser.isPending
              ? t('common.saving')
              : t('users.confirm_delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserDeleteDialog;
